import React, { useState } from "react";
import { FaSearch, FaUndo, FaFilter } from "react-icons/fa";
import Modal from "../shared/Modal";
import { DateRangeField } from "../atoms";

const ReportFilterCard = ({
  title = "Filtre Seçenekleri",
  description = "Rapor için tarih aralığını seçin",
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  onSearch,
  onClear,
  isLoading = false,
  searchButtonText = "Raporu Getir",
  clearButtonText = "Temizle",
  showDatePickers = true,
  searchDisabled = false,
  searchDisabledMessage = "Filtreleme için en az bir alan doldurun",
  // Sayfaya özel seçili filtre chip'leri: [{ label, value }]
  activeFilters = [],
  // Seçilebilecek en erken gün (ör. seçili firmaların oluşturulma günü, #343)
  minDate,
  children,
}) => {
  // Mobilde filtreler ikon butonuna basınca açılan modalda gösterilir
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSearchClick = () => {
    onSearch?.();
    setMobileOpen(false);
  };


  // Filtre gövdesi — masaüstünde inline, mobilde modal içinde render edilir
  const filterBody = (
        <div className="pb-1">
          {showDatePickers && (
            <div className="mb-4">
              <DateRangeField
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                zIndex={10050}
                minDate={minDate}
              />
            </div>
          )}

          {/* Custom Content (Rapor Tipi, Filtreleme Tipi) */}
          {children}

          {/* Actions — mobilde modal dışında ayrı satırda gösterilir.
              DAR KOLONDA SARAR (2026-09-14): bu kart PDF raporlarında ~320px'lik
              kenar sütununda da duruyor. `justify-between` + uzun uyarı metni
              butonları eziyor, "Raporu Getir" iki satıra bölünüyordu. Tailwind
              kırılımları GÖRÜNÜM penceresine baktığı için `sm:` burada işe
              yaramaz; çözüm flex-wrap: yer yoksa metin kendi satırına düşer,
              butonlar bölünmeden yan yana kalır. */}
          <div className="hidden sm:flex flex-wrap items-center gap-x-3 gap-y-2 pt-3 mt-4 border-t border-gray-100">
            {searchDisabledMessage && (
              <p className="text-[11px] text-gray-400 hidden sm:block min-w-0 flex-1 basis-full lg:basis-auto">
                {searchDisabledMessage}
              </p>
            )}
            <div className="flex items-center gap-2 ml-auto shrink-0">
              <button
                onClick={onClear}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors text-xs font-semibold whitespace-nowrap shrink-0"
              >
                <FaUndo className="w-3 h-3" />
                {clearButtonText}
              </button>
              <button
                onClick={handleSearchClick}
                disabled={isLoading || searchDisabled || (showDatePickers && (!startDate || !endDate))}
                className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors text-xs font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed shadow-sm shadow-indigo-200 whitespace-nowrap shrink-0"
              >
                <FaSearch className="w-3 h-3" />
                {searchButtonText}
              </button>
            </div>
          </div>
        </div>
  );

  return (
    <>
      {/* Tek sarmalayıcı: gizli masaüstü bloğu ayrı child olursa parent'ın
          space-y'si mobil butona hayalet üst boşluk bırakıyor. */}
      <div>
        {/* Masaüstü: filtreler inline (box tasarımsız, sayfayla bütünleşik) */}
        <div className="hidden sm:block overflow-visible">{filterBody}</div>

        {/* Mobil: filtre ikonu + Temizle + Raporu Getir yan yana; modal sadece
            filtre alanlarını içerir */}
        <div className="sm:hidden">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-600 shadow-sm active:bg-gray-100 transition-colors text-xs font-semibold shrink-0"
              title="Filtreler"
            >
              <FaFilter className="w-3.5 h-3.5" />
              Filtreler
            </button>
            <button
              type="button"
              onClick={onClear}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 active:bg-gray-200 transition-colors text-xs font-semibold"
            >
              <FaUndo className="w-3 h-3" />
              {clearButtonText}
            </button>
            <button
              type="button"
              onClick={handleSearchClick}
              disabled={isLoading || searchDisabled || (showDatePickers && (!startDate || !endDate))}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg active:bg-indigo-800 transition-colors text-xs font-semibold disabled:bg-gray-300 shadow-sm"
            >
              <FaSearch className="w-3 h-3" />
              {searchButtonText}
            </button>
          </div>
        </div>

        {/* Seçili filtreler — her ekranda filtre alanının altında chip olarak */}
        {((showDatePickers && (startDate || endDate)) ||
          activeFilters.filter((f) => f?.value).length > 0) && (
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {showDatePickers && startDate && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-medium">
                <span className="text-indigo-400">Başlangıç:</span>
                <span className="font-semibold">{new Date(startDate).toLocaleDateString("tr-TR")}</span>
              </span>
            )}
            {showDatePickers && endDate && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-medium">
                <span className="text-indigo-400">Bitiş:</span>
                <span className="font-semibold">{new Date(endDate).toLocaleDateString("tr-TR")}</span>
              </span>
            )}
            {activeFilters
              .filter((f) => f?.value)
              .map((f) => (
                <span
                  key={f.label}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-medium"
                >
                  <span className="text-indigo-400">{f.label}:</span>
                  <span className="font-semibold">{f.value}</span>
                </span>
              ))}
          </div>
        )}
        <Modal open={mobileOpen} onClose={() => setMobileOpen(false)} maxWidth="sm" zIndex={9990}>
          <Modal.Header title={title} subtitle={description} onClose={() => setMobileOpen(false)} />
          <Modal.Body className="max-h-[75vh] overflow-y-auto">
            {filterBody}
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-sm active:bg-indigo-800 transition-colors"
            >
              Tamam
            </button>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default ReportFilterCard;
